import { useState } from 'react'
import { Bell, Building2, ChevronDown, Info } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { SettlementNotificationContactsPanel } from '@/components/finance/settlement-notification-contacts'
import { SettlementNotificationDeliveryLogPanel } from '@/components/finance/settlement-notification-delivery-log'
import { cn } from '@/lib/utils'

type SettlementNotificationsSectionProps = {
  serviceInstanceId?: string | null
  runIdFilter?: string | null
}

export function SettlementNotificationsSection({
  serviceInstanceId,
  runIdFilter,
}: SettlementNotificationsSectionProps) {
  const [open, setOpen] = useState(false)

  if (!serviceInstanceId) {
    return null
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="space-y-1">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Bell className="h-5 w-5" />
                Notification setup
              </CardTitle>
              <CardDescription>
                Configure invoice alert contacts and review delivery logs for this service instance.
              </CardDescription>
            </div>
            <CollapsibleTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2">
                {open ? 'Hide' : 'Show'} contacts &amp; log
                <ChevronDown className={cn('h-4 w-4 transition-transform', open && 'rotate-180')} />
              </Button>
            </CollapsibleTrigger>
          </div>
        </CardHeader>

        <CollapsibleContent>
          <CardContent className="space-y-4 border-t pt-4">
            <div className="flex gap-3 rounded-lg border border-sky-200/80 bg-sky-50/60 p-4 text-sm text-sky-950 dark:border-sky-900/50 dark:bg-sky-950/20 dark:text-sky-100">
              <Info className="mt-0.5 h-4 w-4 shrink-0" />
              <div className="space-y-2">
                <p className="font-medium">How the other company is notified</p>
                <p className="text-sky-900/90 dark:text-sky-100/90">
                  When a settlement invoice is generated, <strong>both</strong> the debtor (from-instance) and creditor
                  (to-instance) notify subscribers configured on <strong>each company&apos;s own</strong> service instance.
                </p>
                <ul className="list-disc space-y-1 pl-5 text-sky-900/90 dark:text-sky-100/90">
                  <li>
                    <strong>Creditor</strong> (<Building2 className="mx-0.5 inline h-3.5 w-3.5" />) — &quot;amount due to you&quot;
                  </li>
                  <li>
                    <strong>Debtor</strong> — &quot;[Copy] amount you owe&quot;
                  </li>
                </ul>
              </div>
            </div>

            <Tabs defaultValue="contacts" className="w-full">
              <TabsList>
                <TabsTrigger value="contacts">Notification contacts</TabsTrigger>
                <TabsTrigger value="log">Delivery log</TabsTrigger>
              </TabsList>
              <TabsContent value="contacts" className="mt-4">
                <SettlementNotificationContactsPanel serviceInstanceId={serviceInstanceId} embedded />
              </TabsContent>
              <TabsContent value="log" className="mt-4">
                <SettlementNotificationDeliveryLogPanel
                  serviceInstanceId={serviceInstanceId}
                  runIdFilter={runIdFilter}
                  embedded
                />
              </TabsContent>
            </Tabs>
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  )
}
